import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  CustomModal,
  type CustomModalOption,
} from "../../shared/components/CustomModal";
import { ValidationModal } from "../../shared/components/ValidationModal";
import { registerAlertBridge } from "../../shared/utils/alertBridge";
import type { AppAlertType } from "../../shared/utils/alertBridge";

type AlertState = {
  visible: boolean;
  title: string;
  message: string;
  type: AppAlertType;
};

type ConfirmState = {
  visible: boolean;
  title: string;
  message: string;
  variant: "default" | "success" | "warning" | "error";
  options: CustomModalOption[];
};

type AlertContextValue = {
  showAlert: (
    title: string,
    message?: string,
    type?: AppAlertType,
    autoCloseMs?: number
  ) => void;
  showConfirm: (config: {
    title: string;
    message: string;
    variant?: ConfirmState["variant"];
    confirmText?: string;
    cancelText?: string;
    destructive?: boolean;
  }) => Promise<boolean>;
};

const AlertContext = createContext<AlertContextValue | null>(null);

export function AlertProvider({ children }: { children: React.ReactNode }) {
  const [alert, setAlert] = useState<AlertState>({
    visible: false,
    title: "",
    message: "",
    type: "info",
  });

  const [confirm, setConfirm] = useState<ConfirmState>({
    visible: false,
    title: "",
    message: "",
    variant: "default",
    options: [],
  });

  const timerRef = useRef<number | null>(null);

  const showAlert = useCallback(
    (
      title: string,
      message?: string,
      type?: AppAlertType,
      autoCloseMs?: number
    ) => {
      const resolvedMessage = message ?? "";
      const resolvedType = type ?? "info";
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      setAlert({
        visible: true,
        title,
        message: resolvedMessage,
        type: resolvedType,
      });
      if (autoCloseMs && autoCloseMs > 0) {
        timerRef.current = window.setTimeout(() => {
          setAlert((prev) => ({ ...prev, visible: false }));
          timerRef.current = null;
        }, autoCloseMs);
      }
    },
    []
  );

  const showConfirm = useCallback(
    (config: {
      title: string;
      message: string;
      variant?: ConfirmState["variant"];
      confirmText?: string;
      cancelText?: string;
      destructive?: boolean;
    }) =>
      new Promise<boolean>((resolve) => {
        const finish = (value: boolean) => {
          setConfirm((prev) => ({ ...prev, visible: false, options: [] }));
          resolve(value);
        };

        setConfirm({
          visible: true,
          title: config.title,
          message: config.message,
          variant: config.variant ?? "default",
          options: [
            {
              text: config.cancelText ?? "Cancelar",
              style: "cancel",
              onPress: () => finish(false),
            },
            {
              text: config.confirmText ?? "Confirmar",
              style: config.destructive ? "destructive" : "default",
              onPress: () => finish(true),
            },
          ],
        });
      }),
    []
  );

  const value = useMemo(
    () => ({ showAlert, showConfirm }),
    [showAlert, showConfirm]
  );

  useEffect(
    () => registerAlertBridge({ showAlert, showConfirm }),
    [showAlert, showConfirm]
  );

  return (
    <AlertContext.Provider value={value}>
      {children}
      <ValidationModal
        open={alert.visible}
        onClose={() => {
          if (timerRef.current) {
            window.clearTimeout(timerRef.current);
            timerRef.current = null;
          }
          setAlert((prev) => ({ ...prev, visible: false }));
        }}
        type={alert.type}
        title={alert.title}
        message={alert.message}
      />
      <CustomModal
        open={confirm.visible}
        title={confirm.title}
        message={confirm.message}
        variant={confirm.variant}
        options={confirm.options}
        onClose={() => {
          confirm.options.find((o) => o.style === "cancel")?.onPress();
        }}
      />
    </AlertContext.Provider>
  );
}

export function useAppAlert(): AlertContextValue {
  const ctx = useContext(AlertContext);
  if (!ctx) {
    throw new Error("useAppAlert must be used within AlertProvider");
  }
  return ctx;
}

/** Compatível com chamadas legadas — use appAlert de shared/utils/alertBridge. */
export type { AppAlertType };
